<template>
  <div class="p-4">
    <div class="mb-4">
      <h4>Service Tokens</h4>
      <p class="text-muted mb-0">
        Mint a scoped, read-only service token used by an outside-agent MCP integration to
        look up customer plan / subscription data. Treat it like a password &mdash; it is
        shown only once, immediately after minting.
      </p>
    </div>

    <div class="d-flex flex-wrap" style="gap: 1.5rem;">
      <!-- Mint token card -->
      <b-card title="Mint a new token" style="max-width: 480px; flex: 1 1 400px;">
        <b-form @submit.prevent="handleMintToken">
          <b-form-group label="Label (optional)" label-for="token-label">
            <b-form-input
              id="token-label"
              v-model="newLabel"
              placeholder="e.g. plan-reader-mcp"
              :disabled="isLoading"
            ></b-form-input>
          </b-form-group>
          <b-form-group label="Expires in (days)" label-for="token-ttl-days">
            <b-form-input
              id="token-ttl-days"
              v-model.number="newTtlDays"
              type="number"
              min="1"
              :disabled="isLoading"
            ></b-form-input>
          </b-form-group>
          <b-button type="submit" variant="primary" :disabled="isLoading">
            <i class="fa fa-plus mr-1"></i> Mint token
          </b-button>
        </b-form>
      </b-card>

      <!-- Revoke all tokens card -->
      <b-card title="Revoke all tokens" style="max-width: 480px; flex: 1 1 400px;">
        <p class="mb-3">
          Immediately invalidates <strong>every</strong> previously minted service token.
          Any integration using an old token will stop working right away. This cannot be
          undone.
        </p>
        <b-button variant="danger" :disabled="isLoading" @click="handleRevokeAllTokens">
          <i class="fa fa-ban mr-1"></i> Revoke all tokens
        </b-button>
      </b-card>
    </div>

    <!-- Generated token modal -->
    <b-modal
      v-model="showGeneratedTokenModal"
      title="Service token minted"
      hide-footer
      no-close-on-backdrop
      @hidden="closeGeneratedTokenModal"
    >
      <div class="text-center">
        <div class="alert alert-warning mb-4">
          <h5><i class="fa fa-exclamation-triangle mr-2"></i>Important!</h5>
          <p class="mb-0">
            <strong>Copy this token now.</strong><br />
            This is the only time the full token value will be shown. It is not stored or
            displayed anywhere else &mdash; if you lose it, mint a new one.
          </p>
        </div>

        <b-form-group label="Token" label-for="generated-token">
          <b-form-input
            id="generated-token"
            :value="generatedToken"
            readonly
            class="text-center font-monospace"
            style="font-size: 14px;"
          ></b-form-input>
        </b-form-group>

        <div class="text-left mb-3">
          <div><strong>Label:</strong> {{ generatedTokenLabel || '(none)' }}</div>
          <div><strong>Expires at:</strong> {{ generatedTokenExpiresAt || '-' }}</div>
        </div>

        <div class="mt-3">
          <b-button variant="info" class="mr-2" @click="copyGeneratedToken">
            <i class="fa fa-copy mr-1"></i> Copy token
          </b-button>
          <b-button variant="success" @click="closeGeneratedTokenModal">
            <i class="fa fa-check mr-1"></i> I've saved it
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  data() {
    return {
      newLabel: '',
      newTtlDays: 90,
      showGeneratedTokenModal: false,
      generatedToken: '',
      generatedTokenLabel: '',
      generatedTokenExpiresAt: '',
    }
  },
  computed: {
    ...mapState('serviceTokens', ['isLoading']),
  },
  methods: {
    ...mapActions('serviceTokens', ['mintToken', 'revokeAllTokens']),
    async handleMintToken() {
      try {
        const response = await this.mintToken({
          label: this.newLabel,
          ttlDays: this.newTtlDays,
        })

        this.generatedToken = response.token || ''
        this.generatedTokenLabel = response.label || this.newLabel || ''
        this.generatedTokenExpiresAt = response.expiresAt || ''
        this.showGeneratedTokenModal = true

        this.newLabel = ''
        this.newTtlDays = 90
      } catch (err) {
        console.error(err)
        this.$toasted.show('Failed to mint service token', {
          type: 'error',
          duration: 3000,
        })
      }
    },
    async handleRevokeAllTokens() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to revoke ALL service tokens? Every previously minted token ' +
          'will stop working immediately, including any in active use. This cannot be undone.',
        {
          title: 'Confirm revoke all tokens',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Revoke all',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        }
      )

      if (!confirmed) {
        return
      }

      try {
        await this.revokeAllTokens()
        this.$toasted.show('All service tokens revoked successfully!', {
          type: 'success',
          duration: 3000,
        })
      } catch (err) {
        console.error(err)
        this.$toasted.show('Failed to revoke service tokens', {
          type: 'error',
          duration: 3000,
        })
      }
    },
    async copyGeneratedToken() {
      if (!this.generatedToken) {
        return
      }

      try {
        await navigator.clipboard.writeText(this.generatedToken)
        this.$toasted.show('Token copied to clipboard!', {
          type: 'success',
          duration: 2000,
        })
      } catch (err) {
        console.error('Failed to copy token:', err)
        const textArea = document.createElement('textarea')
        textArea.value = this.generatedToken
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          this.$toasted.show('Token copied to clipboard!', {
            type: 'success',
            duration: 2000,
          })
        } catch (fallbackErr) {
          this.$toasted.show('Failed to copy token', {
            type: 'error',
            duration: 3000,
          })
        }
        document.body.removeChild(textArea)
      }
    },
    closeGeneratedTokenModal() {
      this.showGeneratedTokenModal = false
      this.generatedToken = ''
      this.generatedTokenLabel = ''
      this.generatedTokenExpiresAt = ''
    },
  },
}
</script>
